import { PageHero, Section } from "../components/primitives.jsx";
import { pageDocs } from "../data/catalog.js";

const colorTokens = [
  ["--carbon", "#252627", "#252627"],
  ["--snow", "#fff9fb", "#fff9fb"],
  ["--alabaster", "#d3d4d9", "#d3d4d9"],
  ["--afb", "#4b88a2", "#4b88a2"],
  ["--brick", "#bb0a21", "#bb0a21"],
  ["--bg-raised", "#2c2d2e", "#2c2d2e"],
  ["--bg-sunken", "#1c1d1e", "#1c1d1e"],
  ["--ink-2", "#b9babe", "#b9babe"],
  ["--ink-3", "#797a7d", "#797a7d"],
  ["--hairline", "rgba(211,212,217,.18)", "rgba(211,212,217,.18)"],
  ["--radius-0", "0 (default)", "transparent"],
  ["--radius-pill", "999px", "transparent"],
  ["--space base", "8px / 4px subgrid", "transparent"],
  ["--ease-emerge", "cubic-bezier(.2,.8,.2,1)", "transparent"],
  ["--dur-std", "220ms", "transparent"],
  ["--container-max", "1280px", "transparent"]
];

const principles = [
  ["Packet over article", "Every unit combines summary, status, examples, side effects, verification, sources, and machine IDs."],
  ["Progressive disclosure", "Lead with verdict and use case. Put examples, diagrams, and long checklists behind clear sections."],
  ["Evidence before advice", "Patterns should show how a reviewer proves the claim in a real codebase."],
  ["Human plus agent parity", "Anything important to a human should also have a stable URI, tag, status, or JSON field."],
  ["Hairline + cell + quiet label", "The KeepStraight signature. Sharp corners, no shadows, no gradients, sentence-case labels, functional motion only."]
];

export function DesignSystemPage() {
  return (
    <main>
      <PageHero eyebrow="Design" title="A specification workbench on the KeepStraight system.">
        <p>
          This surface is built on the KeepStraight design system: Carbon surfaces, hairline cells,
          Instrument Serif display, Geist UI, mono reserved for code. Quiet enough for reading,
          structured enough for agents, explicit enough for founders to act.
        </p>
      </PageHero>

      <Section eyebrow="Principles" title="Composition rules.">
        <div className="principle-grid">
          {principles.map(([title, copy]) => (
            <article key={title} className="cell">
              <h3 className="principle__title">{title}</h3>
              <p>{copy}</p>
            </article>
          ))}
        </div>
      </Section>

      <section className="section">
        <div className="container split">
          <div>
            <span className="lbl">Visual language</span>
            <h2 className="section__title">Color and surface tokens.</h2>
            <p className="split__copy">
              The palette is the KeepStraight five: Carbon (the void), Snow (warm off-white ink),
              Alabaster (hairlines), Air Force Blue (the calm signal), and Brick Red (the one CTA
              on a page). Tokens live in <code>keep-straight-design-system/css/tokens.css</code> —
              this page renders from the same custom properties it documents.
            </p>
          </div>
          <div className="token-board">
            {colorTokens.map(([name, value, swatch]) => (
              <div key={name} className="token" style={{ "--swatch": swatch }}>
                <i className="token__swatch" />
                <span className="token__name">{name}</span>
                <code className="token__value">{value}</code>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Section eyebrow="Per-page documentation" title="Responsibilities, actions, and interactors.">
        <div className="page-doc-grid">
          {pageDocs.map((doc) => (
            <article key={doc.title} className="cell">
              <div className="cell__chrome"><span>{doc.title}</span></div>
              <dl className="page-doc">
                <dt>Purpose</dt><dd>{doc.purpose}</dd>
                <dt>Use cases</dt><dd>{doc.useCases}</dd>
                <dt>Interactors</dt><dd>{doc.interactors}</dd>
              </dl>
            </article>
          ))}
        </div>
      </Section>

      <Section eyebrow="Maintainer actions" title="The design system is editable source, not a screenshot." compact>
        <div className="source-strip">
          <a href="/docs/interface-brief.md">Read interface brief →</a>
          <a href="/data/page-map.json">Update page map →</a>
          <a href="/mcp/resources.json">Check resource fixtures →</a>
          <a href="/data/search-index.json">Check search metadata →</a>
        </div>
      </Section>

      <Section eyebrow="Need-to-element mapping" title="Every user need gets an action target.">
        <div className="table-shell">
          <table>
            <thead><tr><th>User need</th><th>Element</th><th>Action</th></tr></thead>
            <tbody>
              <tr><td>Find why code is hard to test</td><td>Pattern search and status tabs</td><td>Filter for avoid packets and open verification.</td></tr>
              <tr><td>Distinguish critical guidance</td><td>Status and severity badges</td><td>Prioritize critical and required packets first.</td></tr>
              <tr><td>Give an agent repeatable instructions</td><td>Skill package and copy buttons</td><td>Install skill and reference packet URIs.</td></tr>
              <tr><td>Explain risk to a founder</td><td>Case cards and requirement map</td><td>Show side effects, impact, and evidence gate.</td></tr>
              <tr><td>Extend the page model</td><td>Page docs and page-map JSON</td><td>Add purpose, needs, and interactors before building a new page.</td></tr>
            </tbody>
          </table>
        </div>
      </Section>

      <Section eyebrow="Disclosure model" title="What stays visible and what unfolds." compact>
        <div className="table-shell">
          <table>
            <thead><tr><th>Page type</th><th>First view</th><th>Disclosed detail</th><th>Evidence artifact</th></tr></thead>
            <tbody>
              <tr><td>Overview</td><td>Problem, search, primary paths</td><td>Capability cells and source strip</td><td>Search results and source links</td></tr>
              <tr><td>Pattern</td><td>Status, summary, severity</td><td>Examples, side effects, verification</td><td>Copied checklist and resource URI</td></tr>
              <tr><td>Protocol</td><td>Stack and endpoint list</td><td>Request examples and skill install</td><td>JSON fixture URLs</td></tr>
              <tr><td>Case study</td><td>Scenario and diagnosis</td><td>Requirement map and playbook</td><td>Agent handoff packet</td></tr>
            </tbody>
          </table>
        </div>
      </Section>

      <Section eyebrow="Self critique" title="Review passes as QA gates." compact>
        <div className="critique-grid">
          <article className="cell">
            <div className="cell__chrome"><span>Review pass 1</span></div>
            <ul>
              <li>Every major page has at least one stable ID or URI.</li>
              <li>Every reader path ends in a visible action target.</li>
              <li>Every pattern packet has side effects and verification.</li>
            </ul>
          </article>
          <article className="cell">
            <div className="cell__chrome"><span>Review pass 2</span></div>
            <ul>
              <li>MCP fixture names match visible prompt and tool names.</li>
              <li>Search metadata matches visible packet content.</li>
              <li>Skill instructions cite packet URIs instead of vague advice.</li>
            </ul>
          </article>
        </div>
      </Section>
    </main>
  );
}
